import React, { useMemo, memo } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkSlug from 'remark-slug';
import rehypeAutolinkHeadings from 'rehype-autolink-headings';

import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
// import only the languages you need:
import tsx from 'react-syntax-highlighter/dist/esm/languages/prism/tsx';
import typescript from 'react-syntax-highlighter/dist/esm/languages/prism/typescript';
import javascript from 'react-syntax-highlighter/dist/esm/languages/prism/javascript';
import jsx from 'react-syntax-highlighter/dist/esm/languages/prism/jsx';
import python from 'react-syntax-highlighter/dist/esm/languages/prism/python';
import json from 'react-syntax-highlighter/dist/esm/languages/prism/json';
import css from 'react-syntax-highlighter/dist/esm/languages/prism/css';
import bash from 'react-syntax-highlighter/dist/esm/languages/prism/bash';
import markdown from 'react-syntax-highlighter/dist/esm/languages/prism/markdown';
// choose a compact theme - always dark, this is the way
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';


import Copy from '../microComponents/Copy.jsx';

SyntaxHighlighter.registerLanguage('tsx', tsx);
SyntaxHighlighter.registerLanguage('ts', typescript);
SyntaxHighlighter.registerLanguage('typescript', typescript);
SyntaxHighlighter.registerLanguage('js', javascript);
SyntaxHighlighter.registerLanguage('javascript', javascript);
SyntaxHighlighter.registerLanguage('jsx', jsx);
SyntaxHighlighter.registerLanguage('python', python);
SyntaxHighlighter.registerLanguage('json', json);
SyntaxHighlighter.registerLanguage('css', css);
SyntaxHighlighter.registerLanguage('bash', bash);
SyntaxHighlighter.registerLanguage('sh', bash);
SyntaxHighlighter.registerLanguage('shell', bash);
SyntaxHighlighter.registerLanguage('markdown', markdown);
SyntaxHighlighter.registerLanguage('md', markdown);

const MarkdownRenderer = ({ content }) => {
  const components = useMemo(() => {
    return {
    // Typography with DLS-aligned sizing and weights
    h1: props => <h1 className="text-2xl font-normal text-primary mt-8 mb-4 first:mt-0 leading-tight" {...props} />,
    h2: props => <h2 className="text-xl font-normal text-primary mt-6 mb-3 first:mt-0 leading-tight" {...props} />,
    h3: props => <h3 className="text-lg font-normal text-primary mt-5 mb-2 first:mt-0 leading-tight" {...props} />,
    h4: props => <h4 className="text-base font-normal text-primary mt-4 mb-2 first:mt-0 leading-snug" {...props} />,
    
    // Paragraph with optimized line height
    p: props => <p className="text-sm text-primary mb-4 last:mb-0 first:mt-0 leading-relaxed" {...props} />,
    
    // Enhanced links with hover-only underline and visited states
    a: props => {
      const isExternal = props.href?.startsWith('http');
      return (
        <a 
          className="text-accent hover:text-hover visited:text-accent/80 transition-colors duration-200 no-underline hover:underline underline-offset-2"
          {...(isExternal && { target: '_blank', rel: 'noopener noreferrer' })}
          {...props} 
        />
      );
    },
    
    // Enhanced blockquote with accent border and better hierarchy
    blockquote: props => (
      <blockquote className="border-l-4 border-accent/30 pl-4 my-4 first:mt-0 text-secondary italic text-dls-small leading-relaxed" {...props} />
    ),
    
    // Lists with CSS-based depth styling
    ul: props => (
      <ul className="list-disc pl-5 mb-4 first:mt-0 space-y-1.5 [&_ul]:list-[circle] [&_ul]:pl-4 [&_ul_ul]:list-[square]" {...props} />
    ),
    ol: props => (
      <ol className="list-decimal pl-5 mb-4 first:mt-0 space-y-1.5 [&_ol]:list-[lower-alpha] [&_ol]:pl-4 [&_ol_ol]:list-[lower-roman]" {...props} />
    ),
    li: props => <li className="text-sm text-primary leading-normal pl-0.5" {...props} />,
    code({ inline, className, children, ...rest }) {
  const match = /language-(\w+)/.exec(className || '');

  if (!inline && match) {
    return (
      <div
        className="
          rounded-lg
          bg-bg-well border border-border-default
          relative
          max-w-full overflow-x-auto
        "
      >
        {/* 2️⃣  button absolutely positioned */}
        <Copy
          ariaDescription="Copy code block content"
          content={Array.isArray(children) ? children.join('') : String(children)}
          classDefinition="
            absolute top-spacing-1 right-spacing-1                /* align to block’s inner edge */
            h-spacing-8 w-spacing-8
            rounded flex justify-center items-center
            cursor-pointer
            pointer-events-auto             /* re-enable clicks */
            text-secondary
            hover:text-primary
          "
        />
        <SyntaxHighlighter
          style={vscDarkPlus}
          language={match[1]}
          PreTag="div"
          customStyle={{
            margin: 0,
            paddingBlock: 'var(--spacing-3)',
            paddingInline: 'var(--spacing-4)',
            fontSize: '14px',
            lineHeight: '1.45',
            background: 'transparent',
            border: 'none',
            fontFamily: 'var(--font-mono)',
          }}
          {...rest}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      </div>
    );
  }

  /* fallback for inline <code> */
  return (
    <code
      className="bg-bg-well text-primary px-1.5 py-0.5 rounded text-sm font-mono"
      {...rest}
    >
      {children}
    </code>
  );
},
    // Enhanced tables with proper typography and hover states
    table: props => (
      <div className="w-full overflow-x-auto my-4 first:mt-0">
        <table className="min-w-full border-collapse" {...props} />
      </div>
    ),
    thead: props => <thead className="bg-bg-well border-b-2 border-border-divider" {...props} />,
    tbody: props => <tbody className="divide-y divide-border-divider" {...props} />,
    tr: props => <tr className="hover:bg-bg-hover transition-colors duration-200" {...props} />,
    th: props => <th className="px-3 py-2 text-left text-sm font-bold text-primary" {...props} />,
    td: props => <td className="px-3 py-2 text-sm text-primary" {...props} />,
    
    // Images with DLS styling
    img: props => <img className="max-w-full rounded-lg my-4 first:mt-0" loading="lazy" decoding="async" {...props} />,
    
    // Horizontal rule
    hr: props => <hr className="border-border-divider my-8 first:mt-0" {...props} />,
    
    // Strong and emphasis
    strong: props => <strong className="font-bold text-primary" {...props} />,
    em: props => <em className="italic" {...props} />,
    
    // Additional typography elements
    kbd: props => (
      <kbd className="inline-flex items-center px-1.5 py-0.5 text-xs font-mono bg-bg-well text-primary border border-border-default rounded" {...props} />
    ),
    mark: props => (
      <mark className="bg-warning/20 text-primary px-0.5 rounded" {...props} />
    ),
    del: props => (
      <del className="line-through text-secondary" {...props} />
    ),
    ins: props => (
      <ins className="underline text-success decoration-success/50" {...props} />
    ),
    abbr: props => (
      <abbr className="cursor-help underline decoration-dotted underline-offset-2" {...props} />
    ),
    sub: props => <sub className="text-xs" {...props} />,
    sup: props => <sup className="text-xs" {...props} />,
    };
  }, []);

  return (
    <div className="min-w-0 break-words max-w-none prose-sm [&>*:first-child]:mt-0 [&_pre]:m-0 [&_pre]:p-0 leading-[1.6]">
      <Markdown
        remarkPlugins={[remarkGfm, remarkSlug]}
        rehypePlugins={[rehypeAutolinkHeadings]}
        components={components}
      >
        {content}
      </Markdown>
    </div>
  );
};

export default memo(MarkdownRenderer, (prev, next) => prev.content === next.content);
